import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { QRCodeSVG } from 'qrcode.react'
import { STORE_URLS } from '@constants'
import phoneImage from '@assets/images/img_KUDHI.png'

const NAVY = '#002A4E'
const GOLD = '#FFD700'

const PLATFORMS = [
  {
    key: 'android',
    store: 'Google Play',
    caption: 'Disponible sur',
    system: 'Android',
    url: STORE_URLS.android,
    icon: (
      <svg className="h-6 w-6 translate-x-0.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M6 3.5v17a1 1 0 001.5.86l14-8.5a1 1 0 000-1.72l-14-8.5A1 1 0 006 3.5z" />
      </svg>
    ),
  },
  {
    key: 'ios',
    store: 'App Store',
    caption: 'Télécharger dans',
    system: 'iPhone & iPad',
    url: STORE_URLS.ios,
    icon: (
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M16.37 12.6c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.48.83-.72 0-1.82-.81-3-.79-1.54.02-2.96.9-3.76 2.28-1.6 2.78-.41 6.9 1.15 9.16.76 1.1 1.67 2.34 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.77.74 2.98.72 1.23-.02 2.01-1.12 2.76-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.4-.92-2.42-3.66zM14.1 5.86c.63-.77 1.06-1.83.94-2.89-.91.04-2.02.61-2.67 1.37-.58.67-1.1 1.76-.96 2.8 1.02.08 2.06-.52 2.69-1.28z" />
      </svg>
    ),
  },
]

function DownloadAppModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="download-app-title"
    >
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in" onClick={onClose} />

      <div className="home-font relative w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-md sm:rounded-md bg-white shadow-2xl animate-scale-in">
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="grid md:grid-cols-[1fr_1.4fr]">
          {/* Visuel */}
          <div
            className="relative hidden md:flex items-end justify-center overflow-hidden rounded-l-md"
            style={{ background: 'linear-gradient(160deg, #FFE45C 0%, #FFD700 50%, #F5B700 100%)' }}
          >
            <div className="absolute inset-0 home-grid-bg-light" />
            <img src={phoneImage} alt="" className="relative -mb-24 mt-10 h-[420px] w-auto max-w-none rotate-[6deg] drop-shadow-[0_25px_40px_rgba(0,42,78,0.35)]" />
          </div>

          {/* Contenu */}
          <div className="p-7 sm:p-10">
            <p className="text-sm font-bold uppercase tracking-widest" style={{ color: '#B8860B' }}>Application mobile</p>
            <h2 id="download-app-title" className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ color: NAVY }}>
              Téléchargez KUDHI
            </h2>
            <p className="mt-3 text-slate-600">
              Publiez, discutez avec vos clients et suivez vos annonces où que vous soyez. Choisissez votre plateforme.
            </p>

            <div className="mt-7 space-y-3">
              {PLATFORMS.map((p) => (
                <a
                  key={p.key}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 rounded-md border border-slate-200 p-3 pr-4 transition-all hover:-translate-y-0.5 hover:border-transparent hover:shadow-lg"
                >
                  <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-md text-white" style={{ background: NAVY }}>
                    {p.icon}
                  </span>
                  <span className="flex-1 leading-tight">
                    <span className="block text-xs text-slate-500">{p.caption}</span>
                    <span className="block text-lg font-bold" style={{ color: NAVY }}>{p.store}</span>
                    <span className="block text-xs text-slate-400">{p.system}</span>
                  </span>
                  <span className="hidden sm:block rounded-md bg-white p-1 ring-1 ring-slate-200">
                    <QRCodeSVG value={p.url} size={64} level="M" fgColor={NAVY} />
                  </span>
                  <svg className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-1 sm:hidden" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </a>
              ))}
            </div>

            <p className="mt-5 hidden sm:flex items-center gap-2 text-sm text-slate-500">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: GOLD }} />
              Scannez le QR code avec l&apos;appareil photo de votre téléphone.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default DownloadAppModal
